import { useEffect, useState } from "react";
import { toast } from "sonner";

let deferredPrompt = null;
const listeners = new Set();
const emit = () => listeners.forEach((fn) => fn());

export const isStandaloneDisplay = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

export const registerPwa = () => {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    emit();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    emit();
    toast.success("B2B Whitestone installed — find it on your home screen");
  });
  if (!("serviceWorker" in navigator)) return;
  window.addEventListener("load", async () => {
    try {
      const reg = await navigator.serviceWorker.register("/sw.js");
      const promptRefresh = (worker) => {
        toast("New version available", {
          description: "Refresh to get the latest Whitestone portal.",
          duration: Infinity,
          action: { label: "Refresh", onClick: () => worker.postMessage({ type: "SKIP_WAITING" }) },
        });
      };
      if (reg.waiting && navigator.serviceWorker.controller) promptRefresh(reg.waiting);
      reg.addEventListener("updatefound", () => {
        const w = reg.installing;
        if (!w) return;
        w.addEventListener("statechange", () => {
          if (w.state === "installed" && navigator.serviceWorker.controller) promptRefresh(w);
        });
      });
      // Reload only when an UPDATED worker takes over (user tapped Refresh) — never on the very
      // first install, which would wipe whatever the user is typing (e.g. the login form).
      let reloading = false;
      let hadController = !!navigator.serviceWorker.controller;
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (!hadController) { hadController = true; return; }
        if (reloading) return;
        reloading = true;
        window.location.reload();
      });
      setInterval(() => reg.update().catch(() => {}), 60 * 60 * 1000);
    } catch (e) {
      console.warn("SW registration failed", e);
    }
  });
};

// Install button state: true when the browser offers an install prompt and we're not already installed.
export const useInstallPrompt = () => {
  const [, tick] = useState(0);
  useEffect(() => {
    const fn = () => tick((n) => n + 1);
    listeners.add(fn);
    return () => listeners.delete(fn);
  }, []);
  const canInstall = !!deferredPrompt && !isStandaloneDisplay();
  const install = async () => {
    if (!deferredPrompt) return false;
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice.catch(() => null);
    deferredPrompt = null;
    emit();
    return choice?.outcome === "accepted";
  };
  return { canInstall, install, installed: isStandaloneDisplay() };
};

export const useOnline = () => {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); };
  }, []);
  return online;
};
